import { useState, useMemo, useCallback, useEffect } from "react";
import axios from "axios";
import {
  DollarSign, Users, Building2, Save, Loader2,
  TrendingUp, AlertTriangle, Clock, Plus, X, Pencil,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { toast } from "sonner";

import { API } from "./apiConfig";

export function LaborCostTab({ shifts, settings }) {
  const [wages, setWages] = useState({});
  const [departments, setDepartments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [newDept, setNewDept] = useState("");
  const [showDeptMgmt, setShowDeptMgmt] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/settings/wages`, { withCredentials: true }),
      axios.get(`${API}/settings/departments`, { withCredentials: true }),
    ]).then(([wRes, dRes]) => {
      setWages(wRes.data.employee_wages || {});
      setDepartments(dRes.data.departments || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const employeeData = useMemo(() => {
    const included = (shifts || []).filter((s) => s.included);
    const empMap = {};
    included.forEach((s) => {
      if (!empMap[s.emp_name]) {
        empMap[s.emp_name] = { hours: 0, otHours: 0, sickHours: 0, shifts: 0 };
      }
      empMap[s.emp_name].hours += s.elapsed_hours;
      empMap[s.emp_name].shifts += 1;
      if (s.overtime && s.overtime > 0) empMap[s.emp_name].otHours += s.overtime;
      if (s.is_sick) empMap[s.emp_name].sickHours += s.elapsed_hours;
    });

    // Add fixed employees from settings
    const fixedEmps = settings?.fixed_employees || [];
    fixedEmps.forEach((fe) => {
      if (!empMap[fe.name]) {
        empMap[fe.name] = {
          hours: fe.hours || 0, otHours: 0, sickHours: 0, shifts: 0, isFixed: true,
        };
      }
    });

    return Object.entries(empMap)
      .map(([name, data]) => {
        const w = wages[name] || {};
        const wage = w.wage || 0;
        const dept = w.department || "Unassigned";
        const regularH = Math.max(0, data.hours - data.otHours);
        const regCost = regularH * wage;
        const otCost = data.otHours * wage * 1.5;
        return {
          name, department: dept, wage, ...data,
          regularHours: regularH, regularCost: regCost,
          otCost, totalCost: regCost + otCost,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [shifts, settings, wages]);

  const departmentSummary = useMemo(() => {
    const depts = {};
    employeeData.forEach((e) => {
      const d = e.department;
      if (!depts[d]) depts[d] = { employees: 0, hours: 0, cost: 0, otCost: 0 };
      depts[d].employees += 1;
      depts[d].hours += e.hours;
      depts[d].cost += e.totalCost;
      depts[d].otCost += e.otCost;
    });
    return Object.entries(depts).sort((a, b) => b[1].cost - a[1].cost);
  }, [employeeData]);

  const totals = useMemo(() => {
    let totalCost = 0, totalOtCost = 0, totalHours = 0, totalOtHours = 0;
    employeeData.forEach((e) => {
      totalCost += e.totalCost;
      totalOtCost += e.otCost;
      totalHours += e.hours;
      totalOtHours += e.otHours;
    });
    return { totalCost, totalOtCost, totalHours, totalOtHours, employees: employeeData.length };
  }, [employeeData]);

  const updateWage = useCallback((name, field, value) => {
    setWages((prev) => ({
      ...prev,
      [name]: { ...prev[name], [field]: value },
    }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.post(`${API}/settings/wages`, { employee_wages: wages }, { withCredentials: true }),
        axios.post(`${API}/settings/departments`, { departments }, { withCredentials: true }),
      ]);
      toast.success("Wages & departments saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addDepartment = () => {
    const trimmed = newDept.trim();
    if (!trimmed || departments.includes(trimmed)) {
      toast.error(trimmed ? "Department already exists" : "Enter a name");
      return;
    }
    setDepartments((prev) => [...prev, trimmed]);
    setNewDept("");
  };

  const removeDepartment = (dept) => {
    setDepartments((prev) => prev.filter((d) => d !== dept));
  };

  const missingWages = employeeData.filter((e) => !e.wage).length;

  if (!loaded) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-[#0A1F3B]" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="labor-cost-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="labor-summary-cards">
        <SummaryCard
          icon={<DollarSign size={16} />}
          label="Total Labor Cost"
          value={`$${totals.totalCost.toFixed(2)}`}
          accent
        />
        <SummaryCard
          icon={<TrendingUp size={16} />}
          label="Overtime Cost"
          value={`$${totals.totalOtCost.toFixed(2)}`}
          variant={totals.totalOtCost > 0 ? "warning" : ""}
        />
        <SummaryCard
          icon={<Clock size={16} />}
          label="Total Hours"
          value={totals.totalHours.toFixed(2)}
        />
        <SummaryCard
          icon={<Users size={16} />}
          label="Employees"
          value={totals.employees}
        />
      </div>

      {missingWages > 0 && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-800" data-testid="missing-wages-alert">
          <AlertTriangle size={14} className="text-yellow-700 flex-shrink-0" />
          <span>{missingWages} employee{missingWages > 1 ? "s" : ""} missing hourly wage. Set wages below to calculate labor costs.</span>
        </div>
      )}

      {/* Department Summary */}
      {departmentSummary.length > 1 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="dept-summary">
          {departmentSummary.map(([dept, data]) => (
            <div key={dept} className="p-3 bg-white border border-slate-200 rounded-lg" data-testid={`dept-card-${dept.replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-1.5 mb-1.5">
                <Building2 size={12} className="text-[#0A1F3B]" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{dept}</span>
              </div>
              <p className="text-sm font-mono font-bold text-[#0A1F3B]">${data.cost.toFixed(2)}</p>
              <div className="flex gap-3 mt-1 text-[10px] text-slate-500">
                <span>{data.employees} emp</span>
                <span>{data.hours.toFixed(1)}h</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Employee Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="labor-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-[9px] font-bold uppercase tracking-wider text-slate-500">
                <th className="text-left px-3 py-2">Employee</th>
                <th className="text-left px-3 py-2 min-w-[140px]">Department</th>
                <th className="text-right px-3 py-2 min-w-[90px]">Hourly Wage</th>
                <th className="text-right px-3 py-2">Hours</th>
                <th className="text-right px-3 py-2">OT Hrs</th>
                <th className="text-right px-3 py-2">Reg Cost</th>
                <th className="text-right px-3 py-2">OT Cost</th>
                <th className="text-right px-3 py-2">Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {employeeData.map((emp) => (
                <tr
                  key={emp.name}
                  className="border-t border-slate-100 hover:bg-slate-50 transition-colors"
                  data-testid={`labor-row-${emp.name.replace(/\s/g, '-')}`}
                >
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <span className="font-semibold text-[#0A1F3B]">{emp.name}</span>
                      {emp.isFixed && (
                        <span className="text-[8px] bg-green-500/20 text-green-700 px-1 py-0.5 rounded">FIXED</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1.5">
                    <select
                      value={wages[emp.name]?.department || "Unassigned"}
                      onChange={(e) => updateWage(emp.name, "department", e.target.value)}
                      className="w-full h-6 text-[10px] bg-slate-50 border border-slate-200 rounded px-1 text-[#0A1F3B] focus:border-[#0A1F3B] focus:outline-none cursor-pointer"
                      data-testid={`dept-select-${emp.name.replace(/\s/g, '-')}`}
                    >
                      <option value="Unassigned">Unassigned</option>
                      {departments.map((d) => (
                        <option key={d} value={d}>{d}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center justify-end gap-0.5">
                      <span className="text-[10px] text-slate-500">$</span>
                      <input
                        type="number"
                        step="0.25"
                        min="0"
                        value={wages[emp.name]?.wage ?? ""}
                        placeholder="0.00"
                        onChange={(e) => updateWage(emp.name, "wage", e.target.value === "" ? 0 : parseFloat(e.target.value))}
                        className="w-16 h-6 text-[10px] font-mono text-right text-[#0A1F3B] bg-slate-50 border border-slate-200 rounded px-1 focus:border-[#0A1F3B] focus:outline-none"
                        data-testid={`wage-input-${emp.name.replace(/\s/g, '-')}`}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-slate-600">{emp.hours.toFixed(2)}</td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    <span className={emp.otHours > 0 ? "text-orange-700" : "text-slate-500"}>
                      {emp.otHours > 0 ? emp.otHours.toFixed(2) : "-"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono text-slate-600">
                    {emp.wage ? `$${emp.regularCost.toFixed(2)}` : "-"}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">
                    <span className={emp.otCost > 0 ? "text-orange-700" : "text-slate-500"}>
                      {emp.otCost > 0 ? `$${emp.otCost.toFixed(2)}` : "-"}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono font-semibold">
                    <span className={emp.totalCost > 0 ? "text-[#0A1F3B]" : "text-slate-500"}>
                      {emp.totalCost > 0 ? `$${emp.totalCost.toFixed(2)}` : "-"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50">
                <td colSpan={3} className="px-3 py-2 text-right font-bold text-[#0A1F3B] uppercase text-[10px] tracking-wider">Totals</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-[#0A1F3B]">{totals.totalHours.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-orange-700">{totals.totalOtHours.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-[#0A1F3B]">
                  ${(totals.totalCost - totals.totalOtCost).toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold text-orange-700">
                  ${totals.totalOtCost.toFixed(2)}
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold text-[#0A1F3B]">
                  ${totals.totalCost.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Department Management */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="dept-management">
        <button
          onClick={() => setShowDeptMgmt(!showDeptMgmt)}
          className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors"
          data-testid="dept-mgmt-toggle"
        >
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-[#0A1F3B]" />
            <span className="text-xs font-bold text-[#0A1F3B]">Manage Departments</span>
            <span className="text-[10px] text-slate-500">({departments.length})</span>
          </div>
          <Pencil size={12} className="text-slate-500" />
        </button>
        {showDeptMgmt && (
          <div className="px-4 pb-3 border-t border-slate-100">
            <div className="flex items-center gap-2 mt-3 mb-2">
              <Input
                value={newDept}
                onChange={(e) => setNewDept(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addDepartment()}
                placeholder="New department name"
                className="h-7 text-xs bg-slate-50 border-slate-200 text-[#0A1F3B] placeholder:text-slate-500"
                data-testid="new-dept-input"
              />
              <Button
                onClick={addDepartment}
                size="sm"
                className="h-7 px-2 bg-[#0A1F3B] text-white text-[10px] font-bold"
                data-testid="add-dept-btn"
              >
                <Plus size={12} />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {departments.map((dept) => (
                <span
                  key={dept}
                  className="inline-flex items-center gap-1 bg-slate-50 border border-slate-200 rounded px-2 py-1 text-[10px] text-[#0A1F3B]"
                  data-testid={`dept-tag-${dept.replace(/\s/g, '-')}`}
                >
                  {dept}
                  <button
                    onClick={() => removeDepartment(dept)}
                    className="text-slate-500 hover:text-red-700 transition-colors"
                    data-testid={`remove-dept-${dept.replace(/\s/g, '-')}`}
                  >
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#0A1F3B] text-white font-bold gap-1.5 text-xs hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-4px_rgba(10,31,59,0.3)] transition-all"
          data-testid="save-wages-btn"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Wages & Departments
        </Button>
      </div>
    </div>
  );
}

function SummaryCard({ icon, label, value, accent, variant }) {
  const bg = accent
    ? "bg-[#0A1F3B]/5 border-slate-200"
    : variant === "warning"
    ? "bg-orange-500/10 border-orange-500/20"
    : "bg-white border-slate-200";
  const textColor = accent ? "text-[#0A1F3B]" : variant === "warning" ? "text-orange-700" : "text-[#0A1F3B]";

  return (
    <div className={`p-3 border rounded-lg ${bg}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className={accent ? "text-[#0A1F3B]" : "text-slate-500"}>{icon}</span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{label}</span>
      </div>
      <p className={`text-lg font-mono font-bold ${textColor}`}>{value}</p>
    </div>
  );
}
